import { forEach } from 'lodash';

import { OwnedObject, invalidateOwner, Magnitude } from 'gear';

import { Xml } from '../util';

import { TextChar, TextLayout } from '../figure/text';

import { Process } from './process';


const _Scale = function(sx,sy,vAlign) {
  this.sx     = sx     ;
  this.sy     = sy     ;
  this.vAlign = vAlign ;
};
_Scale.prototype = {

  transformer() {
    let pivot;
    switch( this.vAlign ) {
      case 'Baseline': pivot = 'Grip'  ; break ;
      case 'Bottom'  : pivot = 'BottomCenterGrip' ; break ;
      case 'Top'     : pivot = 'TopCenterGrip' ; break ;
      // case 'Middle'  :
      default:         pivot = 'MiddleCenterGrip' ; break ;
    }
    return new TextChar.PivotedScaler(pivot,this.sx,this.sy);
  },
  computePreShift( char) {
    const w = char._frame.width;
    const centerShift = ( ( this.sx - 1 ) / 2 ) * w ;
    switch( this.vAlign ) {
      case 'Baseline': return 0;
      case 'Bottom'  : return centerShift;
      case 'Top'     : return centerShift;
      // case 'Middle'  :
      default:         return centerShift;
    }
  },
  computePostShift( char) {
    const w = char._frame.width, m = char.bearingLX();
    const sx = this.sx;
    const centerShift = ( ( sx - 1 ) / 2 ) * w ;
    switch( this.vAlign ) {
      case 'Baseline': return ( sx - 1 ) * ( w + m );
      case 'Bottom'  : return centerShift;
      case 'Top'     : return centerShift;
      // case VAlignment.Middle  :
      default:         return centerShift;
    }
  },
  clone() {
    return new _Scale(this.sx,this.sy,this.vAlign);
  }
};

const CharScalingProcess = Process.extend({ typeName: 'CharScalingProcess',

  canProcess( /*figure*/) {
    return true;
  },
  process( c ) {
    forEach( c.figures, figure => {
      if( figure instanceof TextLayout ) {
        this.applyTo(figure);
      }
    });
  },
  applyTo( layout ) {

    const lines = layout.textLines();

    for(let i = 0, iEnd = lines.length; i < iEnd ; ++ i ) {
      const lineChars = lines[i]._chars;
      const count = lineChars.length;

      if( count > 0 ) {
        let totalShift = 0;
        const lineIdx = i+1;
        for( let j = 0; j < count; ++j) {

          const char = lineChars[j];

          // Pick the scaling command for this char, if any
          const idx = j+1;
          const scale = this.scaleAt(lineIdx,idx,layout);
          if( scale ) {

            const charData = layout.charData(lineIdx,idx);
            charData._transformers.push( scale.transformer() );

            if( scale.sx !== 1 ) {
              if( j > 0 ) {
                const preShift = scale.computePreShift(char);
                charData._extraKerning += preShift;
                totalShift += preShift;
              }
              if( ( j + 1 ) < count  ) {
                const postShift = scale.computePostShift(char);
                layout.charData(lineIdx,idx+1)._extraKerning += postShift;
                totalShift += postShift;
              }
            }
          }
        }

        // Unless left alignment was choosen, pull back all char to align.
        const hAlign = this._hAlignment;

        let backShift = 0;

        if( hAlign === 'Center' ) {
          backShift = totalShift / 2;
        }
        else if( hAlign === 'Right' ) {
          backShift = totalShift;
        }

        if( backShift !== 0 ) {
          layout.charData(lineIdx,1)._extraKerning -= backShift;
        }
      }

      layout.invalidate();
    }
  }
});


// Capital
//===============================================================================================

const Capital = Process.Capital = CharScalingProcess.extend({ typeName: 'CapitalProcess',

  type: 'Capital',

  Properties: {
    scaleX     : { type: 'number' , def: 1            , onChange: invalidateOwner, dropDef:true },
    scaleY     : { type: 'number' , def: 1            , onChange: invalidateOwner, dropDef:true },
    hAlignment : { type: 'string' , def: 'Center'     , onChange: invalidateOwner, dropDef:true },
    vAlignment : { type: 'string' , def: 'Baseline'   , onChange: invalidateOwner, dropDef:true },
    pattern    : { type: 'string' , def: 'First Last' , onChange: invalidateOwner, dropDef:true }
  },

  requiresText() { return true; },
  outputsText() { return true; },

  // Implement scaleAt used by CharScalingProcess base
  scaleAt(  lineIdx, charIdx, layout ) {
    const pattern = this._pattern;
    const first  = pattern.indexOf('First')  !== -1,
        middle = pattern.indexOf('Middle') !== -1,
        last   = pattern.indexOf('Last')   !== -1;

    const c  = layout.textLines()[lineIdx-1]._chars.length;
    const m  = Math.floor( c / 2 );

    let scaleThis = ( first && charIdx === 1 ) || ( last && charIdx === c );

    if( ! scaleThis && middle ) {
      scaleThis = ( charIdx === m+1 ) ;

      if( ! scaleThis && ( c % 2 === 0 ) ) {
        scaleThis = ( charIdx === m );
      }
    }
    return scaleThis ? new _Scale(this._scaleX, this._scaleY, this._vAlignment) : null;
  }
});
Xml.addFromBuild(Capital);

// BookEnd
//===============================================================================================

const BookEnd = Process.BookEnd = CharScalingProcess.extend({ typeName: 'BookEndProcess',

  type: 'BookEnd',

  Properties: {
    headSX     : { type: 'number'  , def: 1          , onChange: invalidateOwner, dropDef:true },
    headSY     : { type: 'number'  , def: 1          , onChange: invalidateOwner, dropDef:true },
    tailSX     : { type: 'number'  , def: 1          , onChange: invalidateOwner, dropDef:true },
    tailSY     : { type: 'number'  , def: 1          , onChange: invalidateOwner, dropDef:true },
    hAlignment : { type: 'string'  , def: 'Center'   , onChange: invalidateOwner, dropDef:true },
    vAlignment : { type: 'string'  , def: 'Baseline' , onChange: invalidateOwner, dropDef:true }
  },

  requiresText() { return true; },
  outputsText() { return true; },

  // Implement scaleAt used by CharScalingProcess base
  headScale() {
    return new _Scale(this._headSX, this._headSY, this._vAlignment);
  },
  tailScale() {
    return new _Scale(this._tailSX, this._tailSY, this._vAlignment);
  },

  scaleAt( lineIdx, charIdx, layout ) {
    if( charIdx === 1 ) {
      return this.headScale();
    }
    if( charIdx === layout.textLines()[lineIdx-1]._chars.length ) {
      return this.tailScale();
    }
    return null ;
  }

});
Xml.addFromBuild(BookEnd);


// CharsTransform
//===============================================================================================
// This is implemented in the engine using a Generalized Char transformation, replacing
// Char scaling process used in BookEnd and Capital. We may migrate to that design later,
// this is the commit https://github.com/Stahls/cadx/commit/b9fd3c6475e8ee8d0d19bc624de5fc0a794db691
/*
  <CharsTransform>
    <Char Idx="2">
      <TangencialTranslate OffsetH="10%" OffsetV="10%"/>
    </Char>
  </CharsTransform>
*/

export const CharTransform = OwnedObject.extend({ typeName: 'CharTransform', });

const TangencialOffset = CharTransform.TangencialOffset = CharTransform.extend({ typeName: 'TangencialOffsetCharTransform',
  Properties:{
    h: { type: 'magnitude', def: new Magnitude(0.0, 'mm'), onChange: invalidateOwner },
    v: { type: 'magnitude', def: new Magnitude(0.0, 'mm'), onChange: invalidateOwner }
  },
  applyTo( layout,char) {
    const spaceWidth = layout.spaceWidth();
    const h = Magnitude.absoluteMMValueFor(this._h, spaceWidth);
    const v = Magnitude.absoluteMMValueFor(this._v, spaceWidth);
    const displacer = new TextChar.TangencialDisplacer(h,v);
    displacer.transform(char);
    char._charData._transformers.push(displacer);
  }
});
Xml.addFromBuild(TangencialOffset,'TangencialOffset');

CharTransform.parseXml = function( node ) {
  switch(node.nodeName) {
    case 'TangencialOffset': return TangencialOffset.parseXml(node);
  }
  return null;
};


export const CharTransformList = OwnedObject.extend({ typeName: 'CharTransformList',

  Properties:{
    lineIdx: { type: 'integer', def: 1, dropDef: true, onChange: invalidateOwner },
    idx: { type: 'integer', def: 1, onChange: invalidateOwner },
    transforms: { type: 'CharTransform[]', def: [], onChange: invalidateOwner, manualParsing: true }
  },

  setupXml( node ) {
    Xml.setupProperties(node,this);
    forEach( this._transforms, t => {
      t.buildXml(node);
    });
  }
});
CharTransformList.parseXml = function( node ) {
  const p = Xml.parseProperties(node,CharTransformList);

  p.transforms = [];
  forEach( Xml.children_(node), ctNode => {
    const ct = CharTransform.parseXml(ctNode);
    if( ct ) {
      p.transforms.push(ct);
    }
  });
  return new CharTransformList(p);
};
Xml.addFromBuild(CharTransformList,'Char');

const CharsTransform = Process.CharsTransform = Process.extend({ typeName: 'CharsTransformProcess',

  type: 'CharsTransform',

  Properties: {
    chars: { type: 'CharTransformList[]', def: [], owned: true, onChange: invalidateOwner, manualParsing: true }
  },

  requiresText() { return true; },
  outputsText() { return true; },

  setupXml( node ) {
    Xml.setupProperties(node,this);
    forEach( this._chars, t => {
      t.buildXml(node);
    });
  },

  canProcess( /*figure*/) {
    return true;
  },

  process( c ) {
    const _this = this;
    forEach( c.figures, figure => {
      if( figure instanceof TextLayout ) {
        _this.applyTo(figure);
      }
    });
  },
  applyTo( layout) {
    forEach( this._chars, c => {
      const lineIdx = c._lineIdx, idx = c._idx;
      const char = layout.charAt(lineIdx,idx);
      if( char ) {
        forEach( c._transforms, t => {
          t.applyTo(layout,char);
        });
      }
    });
  }
});

CharsTransform.parseXml = function( node ) {
  const p = Xml.parseProperties(node,CharsTransform);
  p.chars = [];
  forEach( Xml.children(node,'Char'), ctNode => {
    const ct = CharTransformList.parseXml(ctNode);
    if( ct ) {
      p.chars.push(ct);
    }
  });
  return new CharsTransform(p);
};
Xml.addFromBuild(CharsTransform);







/** WEBPACK FOOTER **
 ** ./src/cdl/process/text.js
 **/